import type { TrackingPayload } from '../types';
import { sendEvents, type TransportOptions } from './transport';

export type EnqueueOptions = {
  immediate?: boolean;
  preferBeacon?: boolean;
};

export class EventQueue {
  private queue: TrackingPayload[] = [];
  private flushing = false;
  private pendingFlush: false | { preferBeacon: boolean } = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private batchSize: number;
  private flushIntervalMs: number;
  private transport: TransportOptions;

  constructor(
    transport: TransportOptions,
    batchSize: number,
    flushIntervalMs: number
  ) {
    this.transport = transport;
    this.batchSize = batchSize;
    this.flushIntervalMs = flushIntervalMs;
  }

  updateTransport(transport: TransportOptions): void {
    this.transport = transport;
  }

  updateConfig(batchSize: number, flushIntervalMs: number): void {
    this.batchSize = batchSize;
    this.flushIntervalMs = flushIntervalMs;
    this.restartTimer();
  }

  start(): void {
    this.restartTimer();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private restartTimer(): void {
    this.stop();
    if (this.flushIntervalMs <= 0) return;
    this.timer = setInterval(() => {
      void this.flush();
    }, this.flushIntervalMs);
  }

  enqueue(event: TrackingPayload, options: boolean | EnqueueOptions = false): void {
    const opts: EnqueueOptions =
      typeof options === 'boolean' ? { immediate: options } : options;
    this.queue.push(event);
    if (opts.immediate || this.queue.length >= this.batchSize) {
      void this.flush(Boolean(opts.preferBeacon));
    }
  }

  size(): number {
    return this.queue.length;
  }

  async flush(preferBeacon = false): Promise<void> {
    if (this.flushing) {
      this.pendingFlush = {
        preferBeacon: preferBeacon || Boolean(this.pendingFlush && this.pendingFlush.preferBeacon),
      };
      return;
    }
    if (!this.queue.length) return;

    this.flushing = true;
    const batch = this.queue.splice(0, this.queue.length);
    try {
      const ok = await sendEvents(batch, this.transport, preferBeacon);
      if (!ok) {
        // 失败回队头，避免丢数
        this.queue.unshift(...batch);
      }
    } finally {
      this.flushing = false;
      if (this.pendingFlush) {
        const next = this.pendingFlush;
        this.pendingFlush = false;
        if (this.queue.length) {
          await this.flush(next.preferBeacon);
        }
      }
    }
  }
}
